<template>
  <div class="card border-0 shadow-sm mb-4">
    <div class="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
      <h6 class="mb-0 font-weight-bold text-dark">
        <i class="fe fe-printer mr-2 text-primary"></i>Print & Page Layout Settings
      </h6>
      <div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mr-2"
          @click="applyPreset('a4_standard')"
        >
          Preset: A4 (2&times;4 Grid)
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary mr-2"
          @click="applyPreset('cr80_single')"
        >
          Preset: Single PVC Card
        </button>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="saving"
          @click="saveSettingsToBackend"
        >
          <a-spin v-if="saving" size="small" class="mr-1" />
          <i v-else class="fe fe-save mr-1"></i> Save as Default
        </button>
      </div>
    </div>
    <div class="card-body py-3">
      <div class="row">
        <!-- Paper Size -->
        <div class="col-md-3 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Paper Size</label>
          <a-select v-model="localSettings.paper_size" style="width: 100%" @change="onPaperSizeChange">
            <a-select-option value="A4">A4 (210 &times; 297 mm)</a-select-option>
            <a-select-option value="A3">A3 (297 &times; 420 mm)</a-select-option>
            <a-select-option value="Letter">Letter (216 &times; 279 mm)</a-select-option>
            <a-select-option value="Legal">Legal (216 &times; 356 mm)</a-select-option>
            <a-select-option value="CR80">CR80 Single PVC (54 &times; 86 mm)</a-select-option>
            <a-select-option value="Custom">Custom Size</a-select-option>
          </a-select>
        </div>

        <!-- Orientation -->
        <div class="col-md-2 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Orientation</label>
          <a-select v-model="localSettings.orientation" style="width: 100%" @change="emitChange">
            <a-select-option value="portrait">Portrait</a-select-option>
            <a-select-option value="landscape">Landscape</a-select-option>
          </a-select>
        </div>

        <!-- Cards per Row -->
        <div class="col-md-2 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Cards / Row</label>
          <a-input-number
            v-model="localSettings.cards_per_row"
            :min="1"
            :max="8"
            style="width: 100%"
            @change="emitChange"
          />
        </div>

        <!-- Cards per Column -->
        <div class="col-md-2 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Cards / Column</label>
          <a-input-number
            v-model="localSettings.cards_per_column"
            :min="1"
            :max="10"
            style="width: 100%"
            @change="emitChange"
          />
        </div>

        <!-- Background Artwork Switch -->
        <div class="col-md-3 col-12 mb-2 d-flex align-items-center pt-md-3">
          <a-switch
            v-model="localSettings.include_background"
            @change="emitChange"
            class="mr-2"
          />
          <div>
            <span class="font-weight-bold small d-block">Print Background Styling</span>
            <small class="text-muted">Turn off for pre-printed PVC cards</small>
          </div>
        </div>
      </div>

      <!-- Advanced Margins Toggle -->
      <div class="row pt-2 border-top mt-2">
        <div class="col-12">
          <a class="small text-muted font-weight-bold" @click="showAdvanced = !showAdvanced" href="javascript:;">
            <i :class="showAdvanced ? 'fe fe-chevron-up' : 'fe fe-chevron-down'" class="mr-1"></i>
            {{ showAdvanced ? 'Hide Margins & Spacing' : 'Customize Margins & Spacing' }}
          </a>
        </div>
      </div>

      <div class="row mt-2" v-if="showAdvanced">
        <div class="col-md-3 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Top Margin (mm)</label>
          <a-input-number
            v-model="localSettings.margin_top_mm"
            :min="0"
            :max="50"
            :step="0.5"
            style="width: 100%"
            @change="emitChange"
          />
        </div>
        <div class="col-md-3 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Left Margin (mm)</label>
          <a-input-number
            v-model="localSettings.margin_left_mm"
            :min="0"
            :max="50"
            :step="0.5"
            style="width: 100%"
            @change="emitChange"
          />
        </div>
        <div class="col-md-3 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Horizontal Gap (mm)</label>
          <a-input-number
            v-model="localSettings.gap_horizontal_mm"
            :min="0"
            :max="30"
            :step="0.5"
            style="width: 100%"
            @change="emitChange"
          />
        </div>
        <div class="col-md-3 col-6 mb-2">
          <label class="small font-weight-bold mb-1">Vertical Gap (mm)</label>
          <a-input-number
            v-model="localSettings.gap_vertical_mm"
            :min="0"
            :max="30"
            :step="0.5"
            style="width: 100%"
            @change="emitChange"
          />
        </div>

        <div class="col-md-3 col-6 mb-2" v-if="localSettings.paper_size === 'Custom'">
          <label class="small font-weight-bold mb-1">Custom Width (mm)</label>
          <a-input-number
            v-model="localSettings.paper_width_mm"
            :min="10"
            :max="1000"
            style="width: 100%"
            @change="emitChange"
          />
        </div>
        <div class="col-md-3 col-6 mb-2" v-if="localSettings.paper_size === 'Custom'">
          <label class="small font-weight-bold mb-1">Custom Height (mm)</label>
          <a-input-number
            v-model="localSettings.paper_height_mm"
            :min="10"
            :max="1000"
            style="width: 100%"
            @change="emitChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IdCardPrintSettings',
  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      saving: false,
      showAdvanced: false,
      localSettings: {
        paper_size: 'A4',
        orientation: 'portrait',
        cards_per_row: 2,
        cards_per_column: 3,
        margin_top_mm: 5.0,
        margin_left_mm: 5.0,
        gap_horizontal_mm: 4.0,
        gap_vertical_mm: 4.0,
        paper_width_mm: 210.0,
        paper_height_mm: 297.0,
        include_background: true,
      },
    }
  },
  watch: {
    settings: {
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.localSettings = { ...this.localSettings, ...val }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    onPaperSizeChange(val) {
      if (val === 'A4') {
        this.localSettings.cards_per_row = 2
        this.localSettings.cards_per_column = 3
        this.localSettings.margin_top_mm = 5.0
        this.localSettings.margin_left_mm = 5.0
      } else if (val === 'CR80') {
        this.localSettings.cards_per_row = 1
        this.localSettings.cards_per_column = 1
        this.localSettings.margin_top_mm = 0
        this.localSettings.margin_left_mm = 0
        this.localSettings.gap_horizontal_mm = 0
        this.localSettings.gap_vertical_mm = 0
      } else if (val === 'A3') {
        this.localSettings.cards_per_row = 3
        this.localSettings.cards_per_column = 4
      }
      this.emitChange()
    },
    applyPreset(preset) {
      if (preset === 'a4_standard') {
        this.localSettings.paper_size = 'A4'
        this.localSettings.orientation = 'portrait'
        this.localSettings.cards_per_row = 2
        this.localSettings.cards_per_column = 3
        this.localSettings.margin_top_mm = 5.0
        this.localSettings.margin_left_mm = 5.0
        this.localSettings.gap_horizontal_mm = 4.0
        this.localSettings.gap_vertical_mm = 4.0
      } else if (preset === 'cr80_single') {
        this.localSettings.paper_size = 'CR80'
        this.localSettings.orientation = 'portrait'
        this.localSettings.cards_per_row = 1
        this.localSettings.cards_per_column = 1
        this.localSettings.margin_top_mm = 0
        this.localSettings.margin_left_mm = 0
        this.localSettings.gap_horizontal_mm = 0
        this.localSettings.gap_vertical_mm = 0
      }
      this.emitChange()
    },
    emitChange() {
      this.$emit('change', { ...this.localSettings })
    },
    saveSettingsToBackend() {
      this.saving = true
      this.$http
        .post('/api/idcard/settings', this.localSettings)
        .then(({ data }) => {
          this.saving = false
          this.$message.success(data.message || 'Settings saved as default!')
        })
        .catch((error) => {
          this.saving = false
          this.$message.error('Failed to save settings.')
          console.error(error)
        })
    },
  },
}
</script>
